import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { enrichJobsWithLicensedSponsorStatus } from "../../services/licensedSponsorService";
import {
  addStoredJob,
  cleanKeywordList,
  getCvPreview,
  getStoredJobIds,
  getStoredRecommendations,
  JobCard,
  SkillPill,
  SummaryCard,
  toggleStoredJob,
} from "./jobUi";

function toPortalJob(job) {
  return {
    ...job,
    source: job.source || job.source_label || (job.provider === "reed" ? "Jobs by Reed" : "Jobs by Adzuna"),
    summary: job.match_summary || job.description,
    salaryMin: job.salaryMin ?? job.salary_min,
    salaryMax: job.salaryMax ?? job.salary_max,
    matchScore: job.matchScore ?? job.match_score ?? 0,
    skillsMatched: job.skillsMatched || job.matched_skills || [],
    missingKeywords: job.missingKeywords || job.missing_keywords || [],
  };
}

export default function RecommendedJobsPage() {
  const [payload] = useState(() => getStoredRecommendations());
  const cvPreview = getCvPreview();
  const [jobs, setJobs] = useState(() => (payload?.jobs || []).map(toPortalJob));
  const [savedIds, setSavedIds] = useState(() => getStoredJobIds("saved"));
  const [appliedIds, setAppliedIds] = useState(() => getStoredJobIds("applied"));

  useEffect(() => {
    let cancelled = false;

    async function enrich() {
      const portalJobs = (payload?.jobs || []).map(toPortalJob);
      if (!portalJobs.length) {
        setJobs([]);
        return;
      }

      try {
        const enriched = await enrichJobsWithLicensedSponsorStatus(portalJobs);
        if (!cancelled) setJobs(enriched);
      } catch {
        if (!cancelled) setJobs(portalJobs);
      }
    }

    enrich();
    return () => {
      cancelled = true;
    };
  }, [payload]);

  const topRoles = jobs.slice(0, 3);
  const providerLabel = payload?.provider_used === "reed" ? "Jobs by Reed" : "Jobs by Adzuna";
  const topSkills = cleanKeywordList(jobs.flatMap((job) => job.skillsMatched), 8);
  const missingKeywords = cleanKeywordList(jobs.flatMap((job) => job.missingKeywords), 8);

  const saveJob = (jobId) => {
    setSavedIds(toggleStoredJob("saved", jobId));
  };

  const applyJob = (job) => {
    if (job.redirect_url) {
      window.open(job.redirect_url, "_blank", "noopener,noreferrer");
    }
    setAppliedIds(addStoredJob("applied", job.id));
  };

  const handleTailor = () => {
    window.alert("Tailor CV from Jobs Portal is coming soon.");
  };

  if (!cvPreview || !jobs.length) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <p className="text-sm font-bold uppercase tracking-wide text-indigo-600">
          Premium feature
        </p>
        <h1 className="mt-2 text-3xl font-black text-slate-950">
          Upload a CV to view AI-ranked recommendations
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-slate-600">
          Recommendations are created after you upload a CV in the Jobs Portal.
        </p>
        <Link
          to="/jobs"
          className="mt-6 inline-flex rounded-xl bg-indigo-600 px-5 py-3 text-sm font-black text-white transition hover:bg-indigo-700"
        >
          Go to CV upload
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-bold uppercase tracking-wide text-indigo-600">
              {providerLabel}
            </p>
            <h1 className="mt-2 text-3xl font-black text-slate-950">
              AI-ranked recommendations based on your uploaded CV
            </h1>
            <p className="mt-2 max-w-2xl text-slate-600">
              Search focus: {payload.selected_search_role || payload.search_keyword}.
              Always verify job details on the official employer or job board website before applying.
            </p>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <SummaryCard label="Top match score" value={`${jobs[0].matchScore}%`} detail={jobs[0].title} />
        <SummaryCard label="Strong fit roles" value={jobs.filter((job) => job.matchScore >= 85).length} detail="Ranked recommendations" />
        <SummaryCard label="Results source" value={providerLabel.replace("Jobs by ", "")} detail={payload.location || "United Kingdom"} />
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-[0_12px_40px_rgba(15,23,42,0.06)]">
          <h2 className="text-xl font-black text-slate-950">Skills matched</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {topSkills.length ? topSkills.map((skill) => (
              <SkillPill key={skill}>{skill}</SkillPill>
            )) : <span className="text-sm text-slate-500">No exact keyword overlap listed</span>}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-[0_12px_40px_rgba(15,23,42,0.06)]">
          <h2 className="text-xl font-black text-slate-950">Keywords to improve</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {missingKeywords.length ? missingKeywords.map((keyword) => (
              <SkillPill key={keyword} tone="missing">{keyword}</SkillPill>
            )) : <span className="text-sm italic text-slate-500">No major keyword gaps found.</span>}
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-black text-slate-950">Top recommendations</h2>
        {topRoles.map((job) => (
          <JobCard
            key={job.id}
            job={job}
            saved={savedIds.includes(job.id)}
            applied={appliedIds.includes(job.id)}
            onSave={saveJob}
            onApply={applyJob}
            onTailor={handleTailor}
            applyLabel="Apply on official site"
            tailorLabel="Tailor CV coming soon"
          />
        ))}
      </section>
    </div>
  );
}
